import _, {invert, isUndefined, without} from 'lodash';
import {batch} from 'react-redux';

import {VirtualTableSortableColumnDirectionsEnum} from '../../../../../components/common/table/helpers/tableHelpers';
import {CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../../../../helpers/asyncHelpers';
import {buildContactsManagerContactsUrl} from '../../../../../helpers/contactsManagerHelpers';
import {globalNamespace, isTeammateNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {ActionWithPayload} from '../../../../../helpers/reduxHelpers';
import {CardGroupModel} from '../../../../../models/api/cardGroupModel';
import {CardModel} from '../../../../../models/api/cardModel';
import {AppAction} from '../../../../appActions';
import {ThunkAction, ThunkAsyncAction} from '../../../../appThunk';
import {
  fetchAccountCardsAsyncAction,
  fetchAccountIdsByQueryAsyncAction,
  fetchAccountIdsByQueryV2AsyncAction,
  fetchAccountsForCardsAsyncAction,
  fetchAllAccountsAsyncAction,
} from '../../../../data/accounts/accountsActions';
import {
  addCardsToCardGroupsAsyncAction,
  fetchCardGroupsAsyncAction,
  fetchCardsForCardGroupAsyncAction,
  searchCardGroupsAsyncAction,
} from '../../../../data/cardGroups/cardGroupsActions';
import {
  duplicateOrFetchDuplicateCardsAsyncAction,
  fetchCardsByNamespaceAsyncAction,
  FetchCardsByNamespaceOptions,
  fetchCardsByQueryAsyncAction,
  updateCardsToEnhancedAsyncAction,
} from '../../../../data/cards/cardsActions';
import {updateDataAction} from '../../../../data/dataGroupsActions';
import {getUserNamespace} from '../../../../data/teammates/teammatesSelectors';
import {getHasAccountsSearch} from '../../../../featureFlags/featureFlagsSelectors';
import {navigateToLinkAction} from '../../../../stateless/navigationActions';
import {ContactsManagerPanelId} from '../../../panelStateBase';
import {
  getContactsManagerPanel,
  makeGetCardIdFromParameters,
  makeGetContactsManagerPanel,
} from '../contactsManagerPanelSelectors';
import {ContactsManagerPanelState} from '../contactsManagerPanelState';
import {
  ContactsManagerParameters,
  ContactsManagerParametersTypesEnum,
} from '../parameters/contactsManagerParameters';
import {
  ContactsManagerAccountContactsTableState,
  ContactsManagerContactsTableState,
  ContactsManagerTableStatusesEnum,
  ContactsManagerTableTypesEnum,
  tableStateIsAccountContactsTableState,
  tableStateIsAccountsTableState,
  tableStateIsContactsTableState,
  tableStateIsListsTableState,
} from './contactsManagerTableState';

/*
 * Constants.
 */

const defaultTableFetchLimit = 50;

/*
 * Type enum.
 */

export enum ContactsManagerTableActionsEnum {
  // Account Table Actions
  SET_UP_ACCOUNTS_TABLE = '@@panels/contacts-manager/accounts-table/SET_UP_ACCOUNTS_TABLE',
  ADD_ACCOUNT_DETAILS = '@@panels/contacts-manager/accounts-table/ADD_ACCOUNT_DETAILS',
  ADD_SELECTED_ACCOUNT_IDS = '@@panels/contacts-manager/accounts-table/ADD_SELECTED_ACCOUNT_IDS',
  REMOVE_SELECTED_ACCOUNT_IDS = '@@panels/contacts-manager/accounts-table/REMOVE_SELECTED_ACCOUNT_IDS',
  // Contact Table Actions
  SET_UP_CONTACTS_TABLE = '@@panels/contacts-manager/contacts-table/SET_UP_CONTACTS_TABLE',
  ADD_SELECTED_CARD_IDS = '@@panels/contacts-manager/contacts-table/ADD_SELECTED_CARD_IDS',
  REMOVE_SELECTED_CARD_IDS = '@@panels/contacts-manager/contacts-table/REMOVE_SELECTED_CARD_IDS',
  // Card Groups Table Actions
  SET_UP_LISTS_TABLE = '@@panels/contacts-manager/lists-table/SET_UP_LISTS_TABLE',
  SET_UP_SEARCH_CARD_GROUPS_TABLE = '@@panels/contacts-manager/card-groups-table/SET_UP_SEARCH_CARD_GROUPS_TABLE',
  ADD_CARD_GROUP_ID_REFS = '@@panels/contacts-manager/card-groups-table/ADD_CARD_GROUP_ID_REFS',
  ADD_SELECTED_CARD_GROUP_IDS = '@@panels/contacts-manager/card-groups-table/ADD_SELECTED_CARD_GROUP_IDS',
  REMOVE_SELECTED_CARD_GROUP_IDS = '@@panels/contacts-manager/card-groups-table/REMOVE_SELECTED_CARD_GROUP_IDS',
  // Account Contacts Table Actions
  SET_UP_ACCOUNT_CONTACTS_TABLE = '@@panels/contacts-manager/account-contacts-table/SET_UP_ACCOUNT_CONTACTS_TABLE',
  ADD_CARD_ID_REFS = '@@panels/contacts-manager/account-contacts-table/ADD_CARD_ID_REFS',
  // Shared Actions
  CLEANUP = '@@panels/contacts-manager/table/CLEANUP',
  FORCE_REFRESH = '@@panels/contacts-manager/table/FORCE_REFRESH',
  OFFSET_PAGINATED_TABLE_FETCHED = '@@panels/contacts-manager/table/OFFSET_PAGINATED_TABLE_FETCHED',
  TOKEN_PAGINATED_TABLE_FETCHED = '@@panels/contacts-manager/table/TOKEN_PAGINATED_TABLE_FETCHED',
}

/*
 * Types.
 */

interface ContactsManagerTableActionGenericPayload {
  panelId: ContactsManagerPanelId;
}
interface SetUpAccountsTableAction extends ActionWithPayload<ContactsManagerTableActionGenericPayload> {
  type: ContactsManagerTableActionsEnum.SET_UP_ACCOUNTS_TABLE;
}

interface SetUpContactsTableActionPayload extends ContactsManagerTableActionGenericPayload {
  selectedCardIds?: ReadonlyArray<number>;
}

interface SetUpContactsTableAction extends ActionWithPayload<SetUpContactsTableActionPayload> {
  type: ContactsManagerTableActionsEnum.SET_UP_CONTACTS_TABLE;
}

interface SetUpCardGroupsTableActionPayload extends ContactsManagerTableActionGenericPayload {
  cardGroupIds?: ReadonlyArray<number>;
  selectedCardGroupIds?: ReadonlyArray<number>;
}

interface SetUpListsTableAction extends ActionWithPayload<SetUpCardGroupsTableActionPayload> {
  type: ContactsManagerTableActionsEnum.SET_UP_LISTS_TABLE;
}

interface SetUpSearchCardGroupsTableAction extends ActionWithPayload<SetUpCardGroupsTableActionPayload> {
  type: ContactsManagerTableActionsEnum.SET_UP_SEARCH_CARD_GROUPS_TABLE;
}

interface AddAccountTableDetailsActionPayload extends ContactsManagerTableActionGenericPayload {
  accountIds: ReadonlyArray<number>;
  totalResults: number | undefined;
}
interface AddAccountTableDetailsAction extends ActionWithPayload<AddAccountTableDetailsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_ACCOUNT_DETAILS;
}

interface SetUpAccountContactsTableActionPayload extends ContactsManagerTableActionGenericPayload {
  accountId: number;
  selectedCardId: number | undefined;
}
interface SetUpAccountContactsTableAction extends ActionWithPayload<SetUpAccountContactsTableActionPayload> {
  type: ContactsManagerTableActionsEnum.SET_UP_ACCOUNT_CONTACTS_TABLE;
}

interface CardIdRefsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardIds: ReadonlyArray<number>;
}
interface AddCardIdRefsAction extends ActionWithPayload<CardIdRefsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_CARD_ID_REFS;
}

interface CardGroupIdRefsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardGroupIds: ReadonlyArray<number>;
}
interface AddCardGroupIdRefsAction extends ActionWithPayload<CardGroupIdRefsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_CARD_GROUP_ID_REFS;
}

interface CleanupAction extends ActionWithPayload<ContactsManagerTableActionGenericPayload> {
  type: ContactsManagerTableActionsEnum.CLEANUP;
}

interface ForceRefreshAction extends ActionWithPayload<ContactsManagerTableActionGenericPayload> {
  type: ContactsManagerTableActionsEnum.FORCE_REFRESH;
}

interface AddSelectedCardIdsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardIds: ReadonlyArray<number>;
  isInMultiSelectMode: boolean;
}

interface AddSelectedCardIdsAction extends ActionWithPayload<AddSelectedCardIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_IDS;
}

interface RemoveSelectedCardIdsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardIds: ReadonlyArray<number>;
}

interface RemoveSelectedCardIdsAction extends ActionWithPayload<RemoveSelectedCardIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_IDS;
}

interface AddSelectedCardGroupIdsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardGroupIds: ReadonlyArray<number>;
  isInMultiSelectMode: boolean;
}

interface AddSelectedCardGroupIdsAction extends ActionWithPayload<AddSelectedCardGroupIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_GROUP_IDS;
}

interface RemoveSelectedCardGroupIdsActionPayload extends ContactsManagerTableActionGenericPayload {
  cardGroupIds: ReadonlyArray<number>;
}

interface RemoveSelectedCardGroupIdsAction
  extends ActionWithPayload<RemoveSelectedCardGroupIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_GROUP_IDS;
}

interface OffsetPaginationTableFetchedActionPayload extends ContactsManagerTableActionGenericPayload {
  canLoadMore: boolean;
  offset: number;
}

interface OffsetPaginationTableFetchedAction
  extends ActionWithPayload<OffsetPaginationTableFetchedActionPayload> {
  type: ContactsManagerTableActionsEnum.OFFSET_PAGINATED_TABLE_FETCHED;
}

interface TokenPaginationTableFetchedActionPayload extends ContactsManagerTableActionGenericPayload {
  canLoadMore: boolean;
  token?: string;
  type?: ContactsManagerTableTypesEnum;
}

interface TokenPaginationTableFetchedAction
  extends ActionWithPayload<TokenPaginationTableFetchedActionPayload> {
  type: ContactsManagerTableActionsEnum.TOKEN_PAGINATED_TABLE_FETCHED;
}

interface AddOrRemoveSelectedAccountIdsActionPayload extends ContactsManagerTableActionGenericPayload {
  accountIds: ReadonlyArray<number>;
}

interface AddSelectedAccountIdsAction extends ActionWithPayload<AddOrRemoveSelectedAccountIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.ADD_SELECTED_ACCOUNT_IDS;
}

interface RemoveSelectedAccountIdsAction
  extends ActionWithPayload<AddOrRemoveSelectedAccountIdsActionPayload> {
  type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_ACCOUNT_IDS;
}

/*
 * Action creators.
 */

function setUpAccountsTable(panelId: ContactsManagerPanelId): SetUpAccountsTableAction {
  return {
    type: ContactsManagerTableActionsEnum.SET_UP_ACCOUNTS_TABLE,
    payload: {
      panelId,
    },
  };
}

function setUpContactsTableAction(
  panelId: ContactsManagerPanelId,
  selectedCardIds?: ReadonlyArray<number>,
): SetUpContactsTableAction {
  return {
    type: ContactsManagerTableActionsEnum.SET_UP_CONTACTS_TABLE,
    payload: {
      panelId,
      selectedCardIds,
    },
  };
}

function setUpListsTableAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds?: ReadonlyArray<number>,
): SetUpListsTableAction {
  return {
    type: ContactsManagerTableActionsEnum.SET_UP_LISTS_TABLE,
    payload: {
      panelId,
      cardGroupIds,
    },
  };
}

function setUpSearchCardGroupsTableAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds?: ReadonlyArray<number>,
): SetUpSearchCardGroupsTableAction {
  return {
    type: ContactsManagerTableActionsEnum.SET_UP_SEARCH_CARD_GROUPS_TABLE,
    payload: {
      panelId,
      cardGroupIds,
    },
  };
}

function addAccountTableDetails(
  panelId: ContactsManagerPanelId,
  accountIds: ReadonlyArray<number>,
  totalResults: number | undefined,
): AddAccountTableDetailsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_ACCOUNT_DETAILS,
    payload: {
      panelId,
      accountIds,
      totalResults,
    },
  };
}

function setUpAccountContactsTable(
  panelId: ContactsManagerPanelId,
  accountId: number,
  selectedCardId: number | undefined,
): SetUpAccountContactsTableAction {
  return {
    type: ContactsManagerTableActionsEnum.SET_UP_ACCOUNT_CONTACTS_TABLE,
    payload: {
      panelId,
      accountId,
      selectedCardId,
    },
  };
}

function addCardIdRefs(panelId: ContactsManagerPanelId, cardIds: ReadonlyArray<number>): AddCardIdRefsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_CARD_ID_REFS,
    payload: {
      panelId,
      cardIds,
    },
  };
}

function addCardGroupIdRefs(
  panelId: ContactsManagerPanelId,
  cardGroupIds: ReadonlyArray<number>,
): AddCardGroupIdRefsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_CARD_GROUP_ID_REFS,
    payload: {
      panelId,
      cardGroupIds,
    },
  };
}

function cleanup(panelId: ContactsManagerPanelId): CleanupAction {
  return {
    type: ContactsManagerTableActionsEnum.CLEANUP,
    payload: {
      panelId,
    },
  };
}

function forceRefresh(panelId: ContactsManagerPanelId): ForceRefreshAction {
  return {
    type: ContactsManagerTableActionsEnum.FORCE_REFRESH,
    payload: {
      panelId,
    },
  };
}

function addSelectedCardIdsAction(
  panelId: ContactsManagerPanelId,
  cardIds: ReadonlyArray<number>,
  isInMultiSelectMode: boolean,
): AddSelectedCardIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_IDS,
    payload: {
      panelId,
      cardIds,
      isInMultiSelectMode,
    },
  };
}

function removeSelectedCardIdsAction(
  panelId: ContactsManagerPanelId,
  cardIds: ReadonlyArray<number>,
): RemoveSelectedCardIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_IDS,
    payload: {
      panelId,
      cardIds,
    },
  };
}

function addSelectedCardGroupIdsAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds: ReadonlyArray<number>,
  isInMultiSelectMode: boolean,
): AddSelectedCardGroupIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_GROUP_IDS,
    payload: {
      panelId,
      cardGroupIds,
      isInMultiSelectMode,
    },
  };
}

function removeSelectedCardGroupIdsAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds: ReadonlyArray<number>,
): RemoveSelectedCardGroupIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_GROUP_IDS,
    payload: {
      panelId,
      cardGroupIds,
    },
  };
}

function offsetPaginationTableFetchedAction(
  panelId: ContactsManagerPanelId,
  canLoadMore: boolean,
  offset: number,
): OffsetPaginationTableFetchedAction {
  return {
    type: ContactsManagerTableActionsEnum.OFFSET_PAGINATED_TABLE_FETCHED,
    payload: {
      panelId,
      canLoadMore,
      offset,
    },
  };
}

function tokenPaginationTableFetchedAction(
  panelId: ContactsManagerPanelId,
  canLoadMore: boolean,
  token?: string,
  type?: ContactsManagerTableTypesEnum,
): TokenPaginationTableFetchedAction {
  return {
    type: ContactsManagerTableActionsEnum.TOKEN_PAGINATED_TABLE_FETCHED,
    payload: {
      panelId,
      canLoadMore,
      token,
      type,
    },
  };
}

function addSelectedAccountIdsAction(
  panelId: ContactsManagerPanelId,
  accountIds: ReadonlyArray<number>,
): AddSelectedAccountIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.ADD_SELECTED_ACCOUNT_IDS,
    payload: {
      panelId,
      accountIds,
    },
  };
}

function removeSelectedAccountIdsAction(
  panelId: ContactsManagerPanelId,
  accountIds: ReadonlyArray<number>,
): RemoveSelectedAccountIdsAction {
  return {
    type: ContactsManagerTableActionsEnum.REMOVE_SELECTED_ACCOUNT_IDS,
    payload: {
      panelId,
      accountIds,
    },
  };
}

/*
 * Union type.
 */

export type ContactsManagerTableAction =
  | SetUpAccountsTableAction
  | SetUpContactsTableAction
  | AddAccountTableDetailsAction
  | SetUpAccountContactsTableAction
  | AddCardIdRefsAction
  | CleanupAction
  | ForceRefreshAction
  | AddSelectedCardIdsAction
  | RemoveSelectedCardIdsAction
  | AddSelectedCardGroupIdsAction
  | RemoveSelectedCardGroupIdsAction
  | SetUpListsTableAction
  | AddCardGroupIdRefsAction
  | OffsetPaginationTableFetchedAction
  | TokenPaginationTableFetchedAction
  | AddSelectedAccountIdsAction
  | RemoveSelectedAccountIdsAction
  | SetUpSearchCardGroupsTableAction;

/*
 * Actions.
 */

export function contactsManagerSetUpAccountsTableAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(setUpAccountsTable(contactsManagerPanel.id));
  };
}

export function contactsManagerCardGroupPageRefreshAction(): ThunkAction {
  return (dispatch) => {
    batch(() => {
      dispatch(contactsManagerSetUpContactsTableAction());
      dispatch(contactsManagerTableForceRefreshAction());
    });
  };
}

export function contactsManagerSetUpContactsTableAction(
  selectedCardIds?: ReadonlyArray<number>,
  shouldPullCardIdFromRoute?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    const cardIds = shouldPullCardIdFromRoute
      ? _([makeGetCardIdFromParameters()(getState(), {panelId: contactsManagerPanel.id})])
          .compact()
          .value()
      : selectedCardIds;

    dispatch(setUpContactsTableAction(contactsManagerPanel.id, cardIds));
  };
}

export function contactsManagerSetUpListsTableAction(cardGroupIds?: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(setUpListsTableAction(contactsManagerPanel.id, cardGroupIds));
  };
}

export function contactsManagerSetUpSearchCardGroupsTableAction(
  cardGroupIds?: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(setUpSearchCardGroupsTableAction(contactsManagerPanel.id, cardGroupIds));
  };
}

export function contactsManagerTableAddAccountTableDetailsAction(
  accountIds: ReadonlyArray<number>,
  totalResults: number | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(addAccountTableDetails(contactsManagerPanel.id, accountIds, totalResults));
  };
}

export function contactsManagerSetUpAccountContactsTableAction(
  accountId: number,
  selectedCardId: number | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(setUpAccountContactsTable(contactsManagerPanel.id, accountId, selectedCardId));
  };
}

export function contactsManagerAccountsTableFetchedAction(
  accountIds: ReadonlyArray<number>,
  totalResults: number | undefined,
  canLoadMore: boolean,
  token?: string,
  type?: ContactsManagerTableTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    batch(() => {
      dispatch(tokenPaginationTableFetchedAction(contactsManagerPanel.id, canLoadMore, token, type));
      dispatch(addAccountTableDetails(contactsManagerPanel.id, accountIds, totalResults));
    });
  };
}

export function contactsManagerContactsTableFetchedAction(
  cardIds: ReadonlyArray<number>,
  canLoadMore: boolean,
  offset: number,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    batch(() => {
      dispatch(offsetPaginationTableFetchedAction(contactsManagerPanel.id, canLoadMore, offset));
      dispatch(addCardIdRefs(contactsManagerPanel.id, cardIds));
    });
  };
}

export function contactsManagerCardGroupsTableFetchedAction(
  cardGroupIds: ReadonlyArray<number>,
  canLoadMore: boolean,
  token?: string,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    batch(() => {
      dispatch(tokenPaginationTableFetchedAction(contactsManagerPanel.id, canLoadMore, token));
      dispatch(addCardGroupIdRefs(contactsManagerPanel.id, cardGroupIds));
    });
  };
}

export function contactsManagerSearchCardGroupsTableFetchedAction(
  cardGroupIds: ReadonlyArray<number>,
  canLoadMore: boolean,
  token?: string,
  type?: ContactsManagerTableTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    batch(() => {
      dispatch(tokenPaginationTableFetchedAction(contactsManagerPanel.id, canLoadMore, token, type));
      dispatch(addCardGroupIdRefs(contactsManagerPanel.id, cardGroupIds));
    });
  };
}

export function contactsManagerTableAddCardIdRefsAction(cardIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(addCardIdRefs(contactsManagerPanel.id, cardIds));
  };
}

export function contactsManagerTableAddCardGroupIdRefsAction(
  cardGroupIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(addCardGroupIdRefs(contactsManagerPanel.id, cardGroupIds));
  };
}

export function contactsManagerTableAddOtherCardsToGroupsAsyncAction(
  cardGroups: ReadonlyArray<CardGroupModel>,
  cards: ReadonlyArray<CardModel>,
  namespace: string,
  panelId: ContactsManagerPanelId,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});

    if (!contactsManagerPanel) {
      return;
    }

    const cardIds = cards.map((card) => card.id);
    const cardGroupIds = cardGroups.map((group) => group.id);
    const isAllGroupsPrivate = cardGroups.every((group) => isTeammateNamespace(group.namespace));
    const privateNamespace = getUserNamespace(getState());

    // Update the selected cards to be company cards before adding to groups
    if (isAllGroupsPrivate && namespace === globalNamespace) {
      await dispatch(updateCardsToEnhancedAsyncAction(cards));
      await dispatch(addCardsToCardGroupsAsyncAction(cardGroupIds, cardIds));
      await dispatch(contactsManagerTableRefreshWithDelay(panelId));
      return;
    }

    // Duplicate the selected cards to be private cards before adding to groups
    if (isAllGroupsPrivate && isTeammateNamespace(namespace)) {
      const privateCards = await dispatch(
        duplicateOrFetchDuplicateCardsAsyncAction(cards, {
          namespace: privateNamespace,
        }),
      );
      await dispatch(
        addCardsToCardGroupsAsyncAction(
          cardGroupIds,
          privateCards.map((card) => card.id),
        ),
      );
      return;
    }

    // If all groups are not private, simply add the cards to the groups
    // The backend will turn the cards to company cards when adding to company or team groups
    await dispatch(addCardsToCardGroupsAsyncAction(cardGroupIds, cardIds));
    dispatch(contactsManagerTableRefreshWithDelay(panelId));
  };
}

function contactsManagerTableRefreshWithDelay(panelId: ContactsManagerPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});

    if (!contactsManagerPanel) {
      return;
    }

    // TODO: Remove the delay once we find a better way
    await delayAsync(1000); // Wait for cards to be added to groups before triggering a refetch of the table
    dispatch(contactsManagerSetUpContactsTableAction());
  };
}

export function contactsManagerTableCleanupAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(cleanup(contactsManagerPanel.id));
  };
}

export function contactsManagerTableForceRefreshAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(forceRefresh(contactsManagerPanel.id));
  };
}

export function contactsManagerTableAddSelectedCardIdsAction(
  cardIds: ReadonlyArray<number>,
  isInMultiSelectMode?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(maybeNavigateToContactsRouteAction(contactsManagerPanel, cardIds, isInMultiSelectMode));
    dispatch(addSelectedCardIdsAction(contactsManagerPanel.id, cardIds, Boolean(isInMultiSelectMode)));
  };
}

export function contactsManagerTableRemoveSelectedCardIdsAction(cardIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    const {tableState} = contactsManagerPanel;
    if (tableStateIsContactsTableState(tableState)) {
      dispatch(
        maybeNavigateToContactsRouteAction(
          contactsManagerPanel,
          without(tableState.selectedCardIds, ...cardIds),
        ),
      );
    }
    dispatch(removeSelectedCardIdsAction(contactsManagerPanel.id, cardIds));
  };
}

function maybeNavigateToContactsRouteAction(
  contactsManagerPanel: ContactsManagerPanelState,
  cardIds: ReadonlyArray<number>,
  isInMultiSelectMode?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const {parameters, tableState} = contactsManagerPanel;
    const namespace = getUserNamespace(getState());
    const cardGroupId =
      parameters.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS
        ? parameters.cardGroupId
        : undefined;
    const accountId =
      parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
        ? parameters.accountId
        : undefined;

    if (!tableStateIsContactsTableState(tableState) && !tableStateIsAccountContactsTableState(tableState)) {
      return;
    }

    // Check if we should navigate to a route with a card id attached.
    if (shouldNavigateToSelectedCardRoute(tableState, cardIds, isInMultiSelectMode)) {
      const contactsManagerLink = buildContactsManagerContactsUrl(parameters.type, {
        cardId: cardIds[0],
        cardGroupId,
        namespace,
        accountId,
      });
      if (contactsManagerLink) {
        dispatch(navigateToLinkAction(contactsManagerLink));
      }
    }

    // Check if we should navigate to a route without a card id attached.
    if (shouldNavigateToBaseContactsRoute(parameters, tableState, cardIds, isInMultiSelectMode)) {
      const contactsManagerLink = buildContactsManagerContactsUrl(parameters.type, {
        namespace,
        cardGroupId,
        accountId,
      });
      if (contactsManagerLink) {
        dispatch(navigateToLinkAction(contactsManagerLink));
      }
    }
  };
}

export function contactsManagerTableAddSelectedCardGroupIdsAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds: ReadonlyArray<number>,
  isInMultiSelectMode?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return;
    }

    dispatch(
      addSelectedCardGroupIdsAction(contactsManagerPanel.id, cardGroupIds, Boolean(isInMultiSelectMode)),
    );
  };
}

export function contactsManagerTableRemoveSelectedCardGroupIdsAction(
  panelId: ContactsManagerPanelId,
  cardGroupIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return;
    }

    dispatch(removeSelectedCardGroupIdsAction(contactsManagerPanel.id, cardGroupIds));
  };
}

export function contactsManagerTableAccountIdsAddSelectedAction(
  panelId: ContactsManagerPanelId,
  accountIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(addSelectedAccountIdsAction(panelId, accountIds));
  };
}

export function contactsManagerTableAccountIdsRemoveSelectedAction(
  panelId: ContactsManagerPanelId,
  accountIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(removeSelectedAccountIdsAction(panelId, accountIds));
  };
}

/*
 * Table Fetch Actions.
 */

export function contactsManagerFetchAccountContactsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  accountId: number,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsAccountContactsTableState(tableState)) {
      return;
    }

    const fetchOffset = tableState.paginationData?.offset ?? 0;

    /** TODO: Add support for sorting when the endpoint supports it */
    const response = await dispatch(
      fetchAccountCardsAsyncAction(accountId, {
        limit: defaultTableFetchLimit,
        offset: fetchOffset,
        cancelToken,
      }),
    );
    cancelToken.throwIfRequested();

    dispatch(
      contactsManagerContactsTableFetchedAction(
        response.cardIds,
        response.cardIds.length === defaultTableFetchLimit,
        fetchOffset + defaultTableFetchLimit,
      ),
    );
  };
}

export function contactsManagerFetchAccountsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  cancelToken: CancelToken,
  sortBy?: string,
  sortOrder?: VirtualTableSortableColumnDirectionsEnum,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsAccountsTableState(tableState)) {
      return;
    }

    const response = await dispatch(
      fetchAllAccountsAsyncAction({
        limit: defaultTableFetchLimit,
        pageToken: tableState.paginationData?.token,
        cancelToken,
        sortBy,
        sortOrder,
      }),
    );
    cancelToken.throwIfRequested();

    dispatch(
      contactsManagerAccountsTableFetchedAction(
        response.accountIds,
        response.count,
        Boolean(response.next),
        response.next,
      ),
    );
  };
}

export function contactsManagerFetchContactsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  queryOptions: FetchCardsByNamespaceOptions,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsContactsTableState(tableState)) {
      return;
    }

    const {namespace, auto} = queryOptions;
    const fetchOffset = tableState.paginationData?.offset ?? 0;

    const {cards, contacts, cardGroups, limit} = await dispatch(
      fetchCardsByNamespaceAsyncAction({
        limit: defaultTableFetchLimit,
        offset: fetchOffset,
        namespace,
        auto,
        cancelToken,
      }),
    );
    const accounts = await dispatch(fetchAccountsForCardsAsyncAction(cards));
    cancelToken.throwIfRequested();

    batch(() => {
      dispatch(
        updateDataAction({
          cards,
          contacts,
          cardGroups,
          accounts,
        }),
      );
      dispatch(
        contactsManagerContactsTableFetchedAction(
          cards.map((card) => card.id),
          cards.length === limit,
          fetchOffset + defaultTableFetchLimit,
        ),
      );
    });
  };
}

export function contactsManagerFetchListContactsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  cardGroupId: number,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsContactsTableState(tableState)) {
      return;
    }

    const fetchOffset = tableState.paginationData?.offset ?? 0;

    // TODO: remove this delay once we have a better solution
    if (tableState.status === ContactsManagerTableStatusesEnum.FETCH_REQUIRED) {
      await delayAsync(1000);
    } // wait 1 second for the cards to get indexed in elasticsearch

    const {cards, contacts, cardGroups, limit} = await dispatch(
      fetchCardsForCardGroupAsyncAction({
        cardGroupId,
        offset: fetchOffset,
        limit: defaultTableFetchLimit,
      }),
    );
    const accounts = await dispatch(fetchAccountsForCardsAsyncAction(cards));
    cancelToken.throwIfRequested();

    batch(() => {
      dispatch(
        updateDataAction({
          cards,
          contacts,
          cardGroups,
          accounts,
        }),
      );
      dispatch(
        contactsManagerContactsTableFetchedAction(
          cards.map((card) => card.id),
          cards.length === limit,
          fetchOffset + defaultTableFetchLimit,
        ),
      );
    });
  };
}

export function contactsManagerFetchListsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {tableState} = contactsManagerPanel;

    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return;
    }

    const {cardGroups, next} = await dispatch(
      fetchCardGroupsAsyncAction({
        cancelToken,
        limit: defaultTableFetchLimit,
        pageToken: tableState.paginationData?.token,
      }),
    );
    cancelToken.throwIfRequested();

    batch(() => {
      dispatch(
        contactsManagerCardGroupsTableFetchedAction(
          cardGroups.map((group) => group.id),
          Boolean(next),
          next,
        ),
      );
      dispatch(updateDataAction({cardGroups}));
    });
  };
}

export function contactsManagerFetchSearchContactsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  query: string,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {searchState} = contactsManagerPanel;

    if (!searchState) {
      return;
    }

    // If we already loaded everything we can, return early.
    if (
      !isUndefined(searchState?.contactsTableState?.paginationData) &&
      !searchState?.contactsTableState?.paginationData?.canLoadMore
    ) {
      return;
    }

    const fetchOffset = searchState?.contactsTableState?.paginationData?.offset ?? 0;

    const {cards, contacts, limit} = await dispatch(
      fetchCardsByQueryAsyncAction({
        query,
        limit: defaultTableFetchLimit,
        offset: fetchOffset,
        cancelToken,
      }),
    );
    const accounts = await dispatch(fetchAccountsForCardsAsyncAction(cards));
    cancelToken.throwIfRequested();

    batch(() => {
      dispatch(
        updateDataAction({
          cards,
          contacts,
          accounts,
        }),
      );
      dispatch(
        contactsManagerContactsTableFetchedAction(
          cards.map((card) => card.id),
          cards.length === limit,
          fetchOffset + defaultTableFetchLimit,
        ),
      );
    });
  };
}

export function contactsManagerFetchSearchAccountsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  query: string,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {searchState} = contactsManagerPanel;

    if (!searchState) {
      return;
    }

    // If we already loaded everything we can, return early.
    if (
      !isUndefined(searchState?.accountsTableState?.paginationData) &&
      !searchState?.accountsTableState?.paginationData?.canLoadMore
    ) {
      return;
    }

    const pageToken = searchState.accountsTableState?.paginationData?.token;

    const hasAccountsSearch = getHasAccountsSearch(getState());

    // Fetch accounts with SQL.
    const fetchAccountsV1 = async () => {
      const {
        accounts,
        teammates,
        pageToken: nextPageToken,
      } = await dispatch(
        fetchAccountIdsByQueryAsyncAction({
          query,
          limit: defaultTableFetchLimit,
          pageToken,
          cancelToken,
        }),
      );

      // The old search endpoint returned the token under a different param, so we rename it.
      return {accounts, teammates, nextPageToken};
    };

    // Fetch accounts with Elasticsearch.
    const fetchAccountsV2 = async () =>
      await dispatch(
        fetchAccountIdsByQueryV2AsyncAction({
          query,
          limit: defaultTableFetchLimit,
          nextPageToken: pageToken,
          cancelToken,
        }),
      );

    // TODO: Once V2 is released without issues deprecate the original searchAccountsModel and remove the need for this check.
    const {accounts, teammates, nextPageToken} = hasAccountsSearch
      ? await fetchAccountsV2()
      : await fetchAccountsV1();
    cancelToken.throwIfRequested();

    const hasMorePages = Boolean(nextPageToken);

    batch(() => {
      dispatch(
        updateDataAction({
          accounts,
          teammates,
        }),
      );
      dispatch(
        contactsManagerAccountsTableFetchedAction(
          accounts.map((account) => account.id),
          accounts.length,
          hasMorePages,
          nextPageToken,
          ContactsManagerTableTypesEnum.ACCOUNTS,
        ),
      );
    });
  };
}

export function contactsManagerFetchSearchCardGroupsTableDataAsyncAction(
  panelId: ContactsManagerPanelId,
  query: string,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = makeGetContactsManagerPanel()(getState(), {panelId});
    const {searchState} = contactsManagerPanel;

    if (!searchState) {
      return;
    }

    // If we already loaded everything we can, return early.
    if (
      !isUndefined(searchState?.cardGroupsTableState?.paginationData) &&
      !searchState?.cardGroupsTableState?.paginationData?.canLoadMore
    ) {
      return;
    }

    const nextToken = searchState?.cardGroupsTableState?.paginationData?.token;

    const {cardGroups, next} = await dispatch(
      searchCardGroupsAsyncAction(query, {
        limit: defaultTableFetchLimit,
        nextToken,
        cancelToken,
      }),
    );
    cancelToken.throwIfRequested();

    // Need to batch adding groups to data and refs so gc doesn't clean it up.
    batch(() => {
      dispatch(
        contactsManagerSearchCardGroupsTableFetchedAction(
          cardGroups.map((cardGroup) => cardGroup.id),
          Boolean(next),
          next,
          ContactsManagerTableTypesEnum.SEARCH_CARD_GROUPS,
        ),
      );
      dispatch(updateDataAction({cardGroups}));
    });
  };
}

/*
 * Helpers.
 */

function shouldNavigateToSelectedCardRoute(
  tableState: ContactsManagerContactsTableState | ContactsManagerAccountContactsTableState,
  cardIds: ReadonlyArray<number>,
  isInMultiSelectMode?: boolean,
) {
  // If we are in multi select mode we should not navigate.
  if (isInMultiSelectMode || tableState.isInMultiSelectMode) {
    return false;
  }
  // If we are adding more than 1 card, do not navigate.
  if (cardIds.length > 1) {
    return false;
  }
  return true;
}

function shouldNavigateToBaseContactsRoute(
  parameters: ContactsManagerParameters,
  tableState: ContactsManagerContactsTableState | ContactsManagerAccountContactsTableState,
  cardIds: ReadonlyArray<number>,
  isInMultiSelectMode?: boolean,
) {
  // We are already on a base route.
  if (
    (parameters.type === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.OTHER_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS) &&
    !parameters.cardId
  ) {
    return false;
  }
  // If we are in multi select mode we should navigate.
  if (isInMultiSelectMode || tableState.isInMultiSelectMode) {
    return true;
  }
  // If we are adding more than 1 card or have no cards selected, navigate.
  if (cardIds.length > 1 || cardIds.length === 0) {
    return true;
  }
  return false;
}

/*
 * Type guards.
 */

const tableActionsEnumMap = invert(ContactsManagerTableActionsEnum);
export function actionIsTableAction(action: AppAction): action is ContactsManagerTableAction {
  return action.type in tableActionsEnumMap;
}
